import { CSSProperties } from 'react';
import moment from 'moment';
import './data-request-view.style.css';
import { Color, Size } from '@vivli/shared/theme';
import { DownloadOnlyDataPackageFeature } from '@vivli/shared/features/data-package/feature';
import { IDataRequest } from '@vivli/features/data-requests/infrastructure/interface';
import { DataRequestStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { VivliAdminCommentsComponent } from './vivli-admin-comments.component';
import { StudyRowDetailComponent } from './study-row-detail.component';

import { SurveyJsComponent, TeamMemberComponent } from '@vivli/shared/components';
import { useAnalysisPurposeTextMap } from '@vivli/features/data-requests/infrastructure/hook';
import { OutcomesOptions } from '@vivli/features/data-requests/infrastructure/constants';
import { ButtonView } from '@vivli/shared/views';
import { useActiveUserContext, useVivliConfigContext } from '@vivli/core/infrastructure/context';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';

const containerStyle: CSSProperties = {
    flex: 'auto',
    marginBottom: 20,
    paddingLeft: Size.PADDING,
    width: '100%',
    overflow: 'visible',
};

const innerContainerStyle: CSSProperties = {
    paddingLeft: Size.PADDING,
    borderLeft: '1px solid grey',
    display: 'block',
};

const textStyle: CSSProperties = {
    display: 'block',
    whiteSpace: 'pre-wrap',
};

const outerContainerStyle: CSSProperties = {
    marginRight: Size.INSET_SIZE,
    marginLeft: Size.INSET_SIZE,
};

interface DataRequestViewComponentProps {
    dataRequest: IDataRequest;
    isPrintModal?: boolean;
    hideReviewers: boolean;
    onReady?: () => void;
    showAimlQuestions?: boolean;
    includeRiskScore?: boolean;
    handleSavePdfVersion?: () => void;
    isSavingDataRequestVersion?: boolean;
}

export const DataRequestViewComponent = ({
    dataRequest,
    isPrintModal,
    hideReviewers,
    onReady,
    showAimlQuestions,
    includeRiskScore = false,
    handleSavePdfVersion,
    isSavingDataRequestVersion,
}: DataRequestViewComponentProps) => {
    const vivliConfig = useVivliConfigContext();

    if (!dataRequest || !vivliConfig) {
        return null;
    }

    const partner = dataRequest.partner && vivliConfig?.partners?.find((p) => p.name === dataRequest.partner);
    const leadResearcherDetail = dataRequest.researchTeam.researchTeamDetails.find((rtd) => rtd.isLeadResearcher);
    const statisticianDetail = dataRequest.researchTeam.researchTeamDetails.find((rtd) => rtd.isStatistician);

    const analysisPurposeMap = useAnalysisPurposeTextMap();
    const purposeTranslations = dataRequest.researchProposal?.purposeOfAnalysis?.map((a) => analysisPurposeMap(a));

    const selectedOutcomes = OutcomesOptions.filter((o) => dataRequest.researchProposal?.outcomes?.includes(o.value));
    const outcomeTranslations = selectedOutcomes?.map((o) => o.label);

    const language = navigator.language;
    const showDoi: boolean =
        dataRequest.status === DataRequestStatusEnum.Fulfilled || dataRequest.status === DataRequestStatusEnum.PartiallyFulfilled;
    const hasEnquiryIds = dataRequest.enquiryIds.length > 0;

    const template = vivliConfig.surveyTemplates.find((x) => x.surveyVersion === dataRequest.researchProposal.surveyVersion);

    const additionalResearchers = dataRequest.researchTeam.researchTeamDetails.filter(
        (rtd) => !rtd.isLeadResearcher && !rtd.isStatistician
    );
    const { hasPermission } = useActiveUserContext();

    return (
        <div style={outerContainerStyle}>
            <div style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
                <span>
                    <h1 style={{ fontSize: 23 }}>Research Data Request: {dataRequest.requestTitle}</h1>
                </span>
                {hasPermission(PermissionsEnum.DrActAdv) && (
                    <span style={{ alignContent: 'center', marginLeft: 100 }}>
                        <ButtonView style={{ maxWidth: '200px' }} onClick={handleSavePdfVersion} isLoading={isSavingDataRequestVersion}>
                            Save a New PDF Version
                        </ButtonView>
                    </span>
                )}
            </div>
            <h1 style={{ fontSize: 20 }}>{dataRequest.idString}</h1>

            {showDoi && (
                <h1 style={{ fontSize: 23 }}>
                    Data Request DOI: {dataRequest.secondaryAnalysisDoi ? dataRequest.secondaryAnalysisDoi : 'N/A'}
                </h1>
            )}
            <VivliAdminCommentsComponent history={dataRequest.dataRequestHistoryEntries} isPrintModal={isPrintModal} />
            {dataRequest.partner && (
                <h1 style={{ fontWeight: 'bold', fontSize: 20 }}>
                    <span style={{ marginRight: '10px' }}>This request originated in the partner platform:</span>
                    <span style={{ textTransform: 'uppercase' }}>{partner ? partner.displayname : dataRequest.partner}</span>
                </h1>
            )}
            <h1>Research Team</h1>
            <div style={containerStyle}>
                <div className={'page-break-after'}>
                    {dataRequest.researchTeam.leadIsStatistician ? <h2>Lead Investigator and Statistician</h2> : <h2>Lead Investigator</h2>}
                    <TeamMemberComponent hideReviewers={hideReviewers} member={leadResearcherDetail} />
                </div>
                {!dataRequest.researchTeam.leadIsStatistician && (
                    <div className={'page-break-after'}>
                        <h2>Statistician Researcher</h2>
                        <TeamMemberComponent hideReviewers={hideReviewers} member={statisticianDetail} />
                    </div>
                )}
                {additionalResearchers.length > 0 && (
                    <>
                        <h2>Additional Researchers</h2>
                        {additionalResearchers.map((a, i) => {
                            return (
                                <div className={'page-break-after'} key={`${i}-tm`}>
                                    <TeamMemberComponent hideReviewers={hideReviewers} memberName={`Team Member ${i + 1}`} member={a} />
                                </div>
                            );
                        })}
                    </>
                )}
            </div>
            <h1>Research Proposal</h1>
            <div style={containerStyle}>
                <h2>General</h2>
                <div style={innerContainerStyle}>
                    <div className={'page-break-avoid'}>
                        <h3>Title of Proposed Research</h3>
                        {dataRequest.requestTitle}
                        {/* <h3>Requires Ethics Committee or IRB Approval</h3>
                        {dataRequest.researchProposal.irbApprovalRequired ? 'YES' : 'NO'}
                        <div>{dataRequest.researchProposal.furtherDetailForNoIRBApprovalResearch}</div> */}
                        <h3>Lay Summary of the Research Request</h3>
                        <div style={textStyle}>{dataRequest.researchProposal.laySummary}</div>
                        <h3>Aims/Objectives and Hypotheses</h3>
                        <div style={textStyle}>{dataRequest.researchProposal.specificAimsObjectives}</div>
                        <h3>Purpose of Analysis</h3>
                        {purposeTranslations.length > 0 &&
                            purposeTranslations.map((p, index) => (
                                <div style={textStyle} key={`${index}-p`}>
                                    {p === 'Other'
                                        ? `Other: ${
                                              dataRequest.researchProposal.purposeOfAnalysisOtherDescription &&
                                              dataRequest.researchProposal.purposeOfAnalysisOtherDescription
                                          }`
                                        : p}
                                </div>
                            ))}
                    </div>
                    <div className={'page-break-avoid'}>
                        <h3>Outcome(s)</h3>
                        {outcomeTranslations.length > 0 &&
                            outcomeTranslations.map((o, index) => (
                                <div style={textStyle} key={`${index}-o`}>
                                    {o === 'Other' ? `Other: ${dataRequest.researchProposal.outcomesOtherDescription}` : o}
                                </div>
                            ))}
                    </div>
                </div>

                {showAimlQuestions && template && (
                    <div className={'page-break-after page-break-before'}>
                        <h2>AI/ML Question(s)</h2>
                        <div style={innerContainerStyle}>
                            <SurveyJsComponent
                                answers={dataRequest.researchProposal.surveyAnswers}
                                questions={template.surveyTemplate}
                                readonly={true}
                                includeRiskScore={includeRiskScore}
                            />
                        </div>
                    </div>
                )}

                <div className={'page-break-avoid'}>
                    <h2>Study Design</h2>
                    <div style={innerContainerStyle}>
                        <h3>Brief Description</h3>
                        <div style={textStyle}>{dataRequest.researchProposal.researchProposalStudyDesign.briefDescription}</div>
                        <h3>Outcome Elements Categorization/Definitions</h3>
                        <div style={textStyle}>{dataRequest.researchProposal.researchProposalStudyDesign.elementsforOutcomes}</div>
                        <h3>Main Predictor / Independent Variable</h3>
                        <div style={textStyle}>
                            {dataRequest.researchProposal.researchProposalStudyDesign.mainPredictorIndependentVariable}
                        </div>
                        <h3>Other Variables of Interest</h3>
                        <div style={textStyle}>{dataRequest.researchProposal.researchProposalStudyDesign.otherVariablesOfInterest}</div>
                    </div>
                </div>
                <div className={'page-break-avoid'}>
                    <h2>Project Timeline</h2>
                    <div style={innerContainerStyle}>
                        <h3>Target Analysis Start Date</h3>
                        {language === 'en-US'
                            ? moment(dataRequest.researchProposal.projectTimeline.projectStartDate).format('M/D/YY')
                            : moment(dataRequest.researchProposal.projectTimeline.projectStartDate).format('D/M/YY')}
                        <h3>Estimated Analysis Completion Date</h3>
                        {language === 'en-US'
                            ? moment(dataRequest.researchProposal.projectTimeline.analysisCompletionDate).format('M/D/YY')
                            : moment(dataRequest.researchProposal.projectTimeline.analysisCompletionDate).format('D/M/YY')}
                    </div>
                </div>
                <div className={'page-break-avoid'}>
                    <h2>Dissemination and Publication Plan</h2>
                    <div style={innerContainerStyle}>
                        <h3>Plan</h3>
                        <div style={textStyle}>{dataRequest.researchProposal.disseminationAndPublicationPlan}</div>
                        <h3>Citations</h3>
                        <div style={textStyle}>{dataRequest.researchProposal.citations}</div>
                    </div>
                </div>
            </div>
            <div className={'page-break-avoid'}>
                <h1>Statistical Analysis Plan</h1>
                <div style={containerStyle}>
                    <h2>General Plan</h2>
                    <div style={innerContainerStyle}>
                        <div style={textStyle}>{dataRequest.statisticalAnalysisPlan.description}</div>
                    </div>
                    <h2>Countries where analysis will be conducted</h2>
                    <div style={innerContainerStyle}>
                        <div style={textStyle}>{dataRequest.statisticalAnalysisPlan.analysisLocation}</div>
                    </div>
                </div>
            </div>
            <div className={'page-break-avoid'}>
                <h1>Funding</h1>
                <div style={containerStyle}>
                    <h2>General</h2>
                    <div style={innerContainerStyle}>
                        <h3>Government Funding</h3>
                        {dataRequest.funding.researchGrantsFromGovernments ? 'YES' : 'NO'}
                        <div>{dataRequest.funding.researchGrantsFromGovernmentsDetail}</div>
                        <h3>Employment Contracts</h3>
                        {dataRequest.funding.fundingFromEmployers ? 'YES' : 'NO'}
                        <div>{dataRequest.funding.fundingFromEmployersDetail}</div>
                        <h3>Additional Contracts or Consultancies</h3>
                        {dataRequest.funding.additionalContractsAndConsultancies ? 'YES' : 'NO'}
                        <div>{dataRequest.funding.additionalContractsAndConsultanciesDetail}</div>
                        <h3>Commercial Funding</h3>
                        {dataRequest.funding.fundingFromCommercialOrganizations ? 'YES' : 'NO'}
                        <div>{dataRequest.funding.fundingFromCommercialOrganizationsDetail}</div>
                    </div>
                </div>
            </div>
            <h1>Other Information</h1>
            <div style={containerStyle}>
                <div style={innerContainerStyle}>
                    <div style={textStyle}>{dataRequest.otherInformation}</div>
                </div>
            </div>
            <div className={'page-break-avoid'}>
                {hasEnquiryIds && (
                    <div style={{ display: 'flex', flexDirection: 'row' }}>
                        <h1>This request was initiated from enquiry:&nbsp;</h1>
                        {
                            <h1>
                                {' '}
                                {dataRequest.enquiryIds.map((item, index) => (
                                    <span key={index}>
                                        {item}
                                        {index !== dataRequest.enquiryIds.length - 1 ? ', ' : ''}
                                    </span>
                                ))}
                            </h1>
                        }
                    </div>
                )}
            </div>
            <div className={'page-break-avoid'}>
                <h1>Requested Studies</h1>
                <div style={containerStyle}>
                    {dataRequest.requestedStudies.map((study, index) => (
                        <div
                            key={`studyRow-${index}`}
                            style={{ paddingLeft: Size.PADDING, borderLeft: '1px solid grey', marginBottom: 15 }}
                        >
                            <StudyRowDetailComponent
                                key={`${index}-srd`}
                                requestedStudy={study}
                                dataRequestDoi={dataRequest.dataRequestDoi}
                                ipdStatusDescription={''}
                            />
                            {dataRequest.status === DataRequestStatusEnum.Fulfilled && (
                                <div style={{ marginTop: 8, marginBottom: 8, fontSize: 12 }}>
                                    <span style={{ color: Color.DARK_GRAY }}>Study DOI:</span> {study.vivliStudyDoi}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
            <div className={'page-break-avoid'}>
                <h1>Attached Files</h1>
                <div style={containerStyle}>
                    <DownloadOnlyDataPackageFeature dataPackageId={dataRequest.metaDataDataPackageId} onReady={onReady} />
                </div>
            </div>
        </div>
    );
};
